/**
 * Patterns checked against event targets, error messages, and stack frame
 * filenames to decide whether an error should be suppressed in monitoring tools
 * (Sentry, Bugsnag). All strings must be lowercase — comparisons are made after
 * normalizing both sides with .toLowerCase().
 *
 * Add entries here only for confirmed third-party / browser-quirk noise that
 * cannot be fixed in our own code.
 */

/** Matched against DOM target class/id and the error message string. */
export const IGNORED_ERROR_TARGETS: string[] = [
  // Zoho Desk ASAP widget scripts/styles fail to load on iOS/WebKit (including
  // Chrome Mobile iOS). The load failure surfaces as an unhandled promise
  // rejection. Matched against the DOM target selector which contains this id.
  "zohohc-asap",
  // Password manager extensions (LastPass, Dashlane, etc.) inject scripts into
  // login/password-reset pages and use Chrome's internal messaging API. When
  // they can't find an expected DOM object they reject a promise with this
  // string. No stacktrace is available.
  "object not found matching id:",
];

/** Matched against stack frame filenames/URLs. */
export const IGNORED_STACK_FILENAMES: string[] = [
  // Only matches the ASAP bootstrap itself (zohohcasapweb-efc-sdk-*.js).
  "zohohc",
  "zohodesk asap",
  // The widget bundles the bootstrap loads in its second stage are served from
  // static.zohocdn.com/helpcenter/asapweb/js/ as vendor.*.js / efc.*.js, so
  // their filenames contain no "zohohc" substring. Errors thrown while the
  // widget mounts (null i18n store, ZohoDeskAsap.mountComponent) surface from
  // those frames.
  "asapweb",
];

/**
 * Returns true when a thrown or rejected value belongs to one of the ignore
 * lists above. Used by the unhandledrejection listener, which sees the raw
 * value rather than a normalized monitoring event.
 */
export const isIgnorableError = (value: unknown): boolean => {
  const message = (
    value instanceof Error
      ? value.message
      : typeof value === "string"
        ? value
        : ""
  ).toLowerCase();
  const stack = (
    value instanceof Error ? (value.stack ?? "") : ""
  ).toLowerCase();

  // Script/style load failures reject with an Event whose target is the element
  // that failed; its id or class is what identifies the widget.
  const element =
    value instanceof Event && value.target instanceof Element
      ? value.target
      : undefined;
  const target = `${String(element?.className ?? "")} ${element?.id ?? ""}`
    .toLowerCase();

  return (
    IGNORED_ERROR_TARGETS.some(
      (p) => message.includes(p) || target.includes(p),
    ) || IGNORED_STACK_FILENAMES.some((p) => stack.includes(p))
  );
};

/**
 * Returns true when the thrown/rejected value is a non-Error object with no own
 * enumerable keys. The canonical example is a DOM ErrorEvent rejected as a
 * promise: its properties live on the prototype so Object.keys() returns [],
 * and Sentry formats it as
 * "Event `Event` (type=error) captured as promise rejection".
 */
export const isEmptyNonErrorObject = (value: unknown): boolean => {
  return (
    value !== null &&
    typeof value === "object" &&
    !(value instanceof Error) &&
    Object.keys(value as Record<string, unknown>).length === 0
  );
};
